import React, { useMemo, useState } from 'react';

/* ===== helpers mínimos ===== */
const resolveApiBase = (apiBaseProp) => {
  const fromProp = (apiBaseProp || '').trim();
  if (fromProp) return fromProp.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};

const fetchJSON = async (url, opts = {}) => {
  const resp = await fetch(url, { credentials: 'include', ...opts });
  let j = null; try { j = await resp.json(); } catch {}
  if (!resp.ok || (j && j.ok === false)) {
    const msg = (j && j.msg) || resp.statusText || `HTTP ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status;
    err.payload = j;
    throw err;
  }
  return j ?? {};
};
/* =========================== */

export default function OrdenWebDetallePendiente({ API_BASE, fileUrl, pedido, onClose, onEstadoCambiado }) {
  const base = useMemo(() => resolveApiBase(API_BASE), [API_BASE]);
  const [busy, setBusy] = useState(false);

  const patchEstado = async (estado) => {
    const url = `${base}/api/web/pedidos/id/${pedido.id}/estado`;
    // debug útil en dev
    console.log('[PATCH estado] ->', url, { estado });
    return fetchJSON(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ estado }),
    });
  };

  const validar = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const nuevo = Number(pedido.total || 0) < 1000 ? 'enProceso' : 'enRuta';
      await patchEstado(nuevo);
      onEstadoCambiado(pedido.id, nuevo);
    } catch (e) {
      console.error('[VALIDAR] error:', e);
      alert(e.message || 'No se pudo actualizar');
    } finally {
      setBusy(false);
    }
  };

  const cancelarPedido = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await patchEstado('Cancelado');
      onEstadoCambiado(pedido.id, 'Cancelado');
    } catch (e) {
      console.error('[CANCELAR] error:', e);
      alert(e.message || 'No se pudo cancelar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Pedido #${pedido.id} (Pendiente)`}>
      <InfoCliente p={pedido} />
      <InfoPago p={pedido} />
      <Productos p={pedido} API_BASE={base} fileUrl={fileUrl} />
      <Voucher   p={pedido} API_BASE={base} fileUrl={fileUrl} />

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200" disabled={busy}>
          Cerrar
        </button>
        <button
          onClick={cancelarPedido}
          className="px-4 py-2 rounded-xl border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          disabled={busy}
        >
          Cancelar pedido
        </button>
        <button
          onClick={validar}
          className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          disabled={busy}
        >
          {busy ? 'Validando...' : 'Validar'}
        </button>
      </div>
    </Modal>
  );
}

/* Compartidos */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200">Cerrar</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export function InfoCliente({ p }) {
  return (
    <Card title="Cliente">
      <Item k="Tipo" v={p.tipoPedido === 'sinUsuario' ? 'Invitado' : `Cliente #${p.usuarioId}`} />
      <Item k="Nombre" v={p.cliente} />
      <Item k="DPI" v={p.dpiCliente} />
      <Item k="WhatsApp" v={p.whatsapp} />
      <Item k="Dirección" v={p.direccionEntrega} />
    </Card>
  );
}

export function InfoPago({ p }) {
  return (
    <Card title="Pago">
      <Item k="Boleta" v={p.boleta || '—'} />
      <Item k="Banco" v={p.banco || '—'} />
      <Item k="Total" v={`Q${Number(p.total || 0).toFixed(2)}`} />
      <Item k="Fecha" v={safeDate(p.created_at)} />
      <Item k="Estado" v={p.estado} />
    </Card>
  );
}

export function Productos({ p /*, API_BASE*/, fileUrl }) {
  const items = Array.isArray(p.pedido) ? p.pedido : (p.pedido?.items || []);
  if (!items.length) return <Card title="Productos"><div className="text-sm text-gray-500">Sin items.</div></Card>;
  return (
    <Card title="Productos">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((it, idx) => {
          // Si quieres usar imágenes en el futuro:
          // const img = it.imagen; const src = fileUrl(img);
          return (
            <div key={idx} className="border rounded-lg p-3">
              <div className="text-sm font-semibold">{it.nombre}</div>
              <div className="mt-2 text-sm text-gray-600">Cantidad: {it.cantidad ?? it.qty ?? 1}</div>
              {(it.precio || it.price) && <div className="text-sm">Precio: Q{Number(it.precio || it.price).toFixed(2)}</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function Voucher({ p, API_BASE, fileUrl }) {
  const src = p.imagen ? fileUrl(p.imagen) : null;
  return (
    <Card title="Voucher / Comprobante">
      {src
        ? (
          <div>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:underline"
            >
              Abrir voucher
            </a>
          </div>
        ) : <div className="text-sm text-gray-500">Sin imagen.</div>}
    </Card>
  );
}

export function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <div className="text-gray-500">{k}</div>
      <div className="font-medium text-gray-800 text-right ml-4 break-words">{v ?? '—'}</div>
    </div>
  );
}

function safeDate(d){ try { return new Date(d).toLocaleString(); } catch { return '—'; } }
